import React, { Component } from 'react';
import {
  Col,
  Row,
  Card,
  Button,
} from "react-bootstrap";
import { toast } from 'react-toastify';
import { withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import _ from "lodash";
import moment from "moment";
import HistoryCard from "../../components/dashboard/HistoryCard";
import SettingsItem from "../../components/settings/SettingsItem";
import InvoiceItem from "../../components/settings/InvoiceItem";
import { getInvoices } from "../../actions/settings";
import APIUrls from '../../constants/ApiUrls';
import { APIManager } from "../../utils/api";
import { 
  STRIPE_STATUS, 
  isPartnerCustomer, 
  partnerBillingUrl, 
  getActiveSubscriptionFromCustomer, 
  getLastSubscriptionFromCustomer } from "../../utils/billing";
import TeamProfile from "./TeamProfile";
import './Billing.scss';

const PlanPeriod = {
  'YEAR': 'year',
  'MONTH': 'month'
};

const sortAmount = (p1, p2) => {
  if (p1.metadata.sort) {
    if (p1.metadata.sort > p2.metadata.sort) {
      return 1;
    } else if (p1.metadata.sort === p2.metadata.sort) {
      return 0;
    } else {
      return -1
    }
  } else {
    if (p1.amount > p2.amount) {
      return 1;
    } else if (p1.amount === p2.amount) {
      return 0;
    } else {
      return -1
    }
  }
}

// Stripe -- it's safe to include this key here
const stripe = Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY); //eslint-disable-line no-undef

class Billing extends Component {
  constructor(props) {
    super(props);

    this.state = {
      customerDetails: null,
      isPartnerCustomer: false,
      yearPlans: [],
      monthPlans: []
    };
  }

  async componentDidMount() {
    // From willMount
    this.props.getInvoices(_.get(this.props.user, 'customerId', ''));

    await this.refreshAuthorizedRequestConfig();

    const { customerId, tenantId } = this.props.user;
    this.setState({ isLoading: true });

    let response = {};
    try {
      response = await APIManager.Instance.call("GET", APIUrls.getCustomerDetail(customerId, tenantId), this.axiosConfig);
      this.setState({ customerDetails: response });
      this.setState({ isPartnerCustomer: isPartnerCustomer(response) });

      let allPlans = {};
      allPlans = await this.getPlans(response);
      this.setState({ yearPlans: allPlans.yearlyPlans, monthPlans: allPlans.monthlyPlans });
    } catch (e) {
      this.setState({ isLoading: false });
      toast.error('Failed to get all billing info: ' + e.message, {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

  }

  /**
   * Refresh tokens for requests
   * @returns (Object) jwt tokens
   */
   refreshAuthorizedRequestConfig = async () => {
    const tokens = await this.props.Auth.currentSession();
    
    this.axiosConfig = {
      headers: {
        'Authorization': `Bearer ${tokens.accessToken.jwtToken}`,
        'X-Identity': tokens.idToken.jwtToken,
        "Content-Type": "application/json",
      },
      crossdomain: true
    };

    return tokens;
  }

  updatePlan = async () => {
    if (this.state.isPartnerCustomer) {
      window.location.href = partnerBillingUrl(this.state.customerDetails);
    } else {
      this.props.history.push("/paywall");
    }

  }

  onChangePaymentMethod = async () => {
    const { customerDetails } = this.state;

    // If the customer is a partner customer, redirect to the billing page
    if (this.state.isPartnerCustomer) {
      window.location.href = partnerBillingUrl(customerDetails);
      return;
    }

    // else Customer Portal
    await this.refreshAuthorizedRequestConfig();

    const { customerId, tenantId } = this.props.user;
    // _.get(customerDetails, 'subscriptions.data[0].plan.id', '');
    const planId = _.get(getActiveSubscriptionFromCustomer(customerDetails, true), 'plan.id', ''); 

    try {
      // update only, redirect to customer portal
      const response = await APIManager.Instance.call("GET", APIUrls.getCheckoutSession(customerId, planId, tenantId, process.env.REACT_APP_API_CLIENT_BASE_URL, true), this.axiosConfig);
      window.location.href = response.url;
    } catch (error) {
      toast.error('Failed to get checkout sessions: ' + error.message, {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  }

  /**
   * Get all the plans from Stripe
   * Break into monthly and yearly plans
   * Flag the current customer's plan
   * @returns (Object) {monthlyPlans, yearlyPlans, currentPlanInterval}
   */
   getPlans = async (customerDetails) => {
    await this.refreshAuthorizedRequestConfig();
    const response = await APIManager.Instance.call("GET", APIUrls.getPlansList, this.axiosConfig);
    
    // get the active sub
    let activeSubscription = getActiveSubscriptionFromCustomer(customerDetails, true);
    
    // get the current plan interval
    let currentPlanInterval = _.get(activeSubscription, 'plan.interval', ''); //_.get(customerDetails, 'subscriptions.data[0].plan.interval', '');

    // find and flag the current plan description
    // there may not be a current plan if owner is on an expired trial
    // or has previously canceled
    const currentPlanId = _.get(activeSubscription, 'plan.id', ''); //_.get(customerDetails, 'subscriptions.data[0].plan.id', '');
    const currentPlan = response.find(p => p.id === currentPlanId);
    if (currentPlan) {
      currentPlan.customerPlan = true;
    }

    // filter out non-plan products
    // we only offer one product, so this selects for the family (eg Standard vs Parnter) product
    
    const availablePlans = response.filter(p => p.product === currentPlan.product);
    
    // break out plans by interval including the selected plan even if not active
    const yearlyPlans = availablePlans.filter(p => {
      return (p.interval === PlanPeriod.YEAR) && (p.active || p.customerPlan)
    }).sort(sortAmount);

    const monthlyPlans = availablePlans.filter(p => {
      return (p.interval === PlanPeriod.MONTH) && (p.active || p.customerPlan)
    }).sort(sortAmount);

    const allPlans = { monthlyPlans, yearlyPlans, currentPlanInterval}
    return allPlans;
  } 

  partnerPlanInfo = () => {
    const activeSubscription = getActiveSubscriptionFromCustomer(this.state.customerDetails, true);
    const planMetadata = _.get(activeSubscription, 'plan.metadata', {});
    const planCards = [];
    for (const plan of this.state.monthPlans) {
      const features = [];
      for (const feature of Object.keys(plan.metadata).sort()) {
        if (feature.startsWith("feature")) {
          features.push(<div className="feature-item" key={feature}>{plan.metadata[feature]}</div>)
        }
      }
      planCards.push(
        <Col>
        <Card>
         <Card.Header>{_.get(plan, 'nickname', 'AppSumo Plan')}</Card.Header>
         <Card.Body>
           <Card.Title>{_.get(plan, 'metadata.description', '')}</Card.Title>
           <div className="plan-details">
             { features } 
           </div>
           <br/>
           <Button variant="outline-danger" onClick={this.updatePlan}>{ plan.customerPlan ? "VIEW" : "SELECT" }</Button>
         </Card.Body>
       </Card>
       </Col>
      );
    }
    return planCards;
  }

  render () {
    // const isAdmin = _.get(this.props, "cognitoUser.attributes['custom:role']", '') === 'admin';
    const activeSubscription = getActiveSubscriptionFromCustomer(this.state.customerDetails, true);
    const cardInfo = _.get(this.state.customerDetails, 'card', null);
    let untilTimeframe;
    
    if (this.state.isPartnerCustomer) {
      untilTimeframe = "LIFETIME 🌈"
      const billingUpdateUrl = partnerBillingUrl(this.state.customerDetails);
    
    } else {
      untilTimeframe = moment(_.get(activeSubscription, 'current_period_end', '')*1000).format('MMM DD, YYYY');
    }

    let paymentInformation = '';

    if (this.state.isPartnerCustomer) {
      paymentInformation = "Paid via AppSumo";
    } else {
      if (!cardInfo) {
        paymentInformation = 'No Payment Method'
      } else {
        paymentInformation = `${_.get(cardInfo, 'brand', '').toUpperCase()} **** **** **** ${_.get(cardInfo, 'last4', '')}`;
      }
    }
    

    return (
      <div className="billing-container">
        <TeamProfile />
        <div className="billing-content">
          <Row className="full-width">
            <div className="subscription-section">
              <Card bg="light" className="full-width full-height">
                <Card.Header>Subscription</Card.Header>
                <Card.Body>
                  <SettingsItem
                    title="PLAN"
                    value={_.get(activeSubscription, 'plan.nickname', '')}
                    buttonTitle="UPDATE"
                    onClick={this.updatePlan}
                  />
                  <SettingsItem
                    title="THROUGH"
                    value={untilTimeframe}
                  />
                  <SettingsItem
                    title="PAYMENT DETAILS"
                    value={paymentInformation}
                    buttonTitle="CHANGE"
                    onClick={this.onChangePaymentMethod}
                  />
                </Card.Body>
              </Card>
            </div>
          </Row>
          { !this.state.isPartnerCustomer && <Row className="full-width">
            <Col>
             <Card bg="light" className="full-width full-height">
                 <Card.Header>Billing History</Card.Header>
                  <Card.Body>
                    <div className="billing-history">
                      {
                        this.props.billingHistory.map(history => {
                          const planName = _.get(history, 'lines.data[0].plan.nickname', '');
                          const { end, start } = _.get(history, 'lines.data[0].period', {});
                          const period = `${moment(start * 1000).format('YYYY-MM-DD')} to ${moment(end * 1000).format('YYYY-MM-DD')}`;
                          const amount = _.get(history, 'lines.data[0].amount', 0);
                          const userCount = _.get(history, 'lines.data[0].quantity', 0);
                          const invoiceUrl = _.get(history, 'invoice_pdf', '');

                          return (
                            <InvoiceItem
                              key={history.id}
                              period={period}
                              planName={planName}
                              userCount={userCount}
                              amount={amount}
                              invoiceUrl={invoiceUrl}
                            />
                          )
                        })
                      }
                    </div>
                    {this.props.billingHistory.length === 0 &&
                      <div className="empty-billing-history">
                        -- No billing history yet --
                      </div>
                    }
                </Card.Body>
              </Card>
              
            </Col>
          </Row>}
          { this.state.isPartnerCustomer && <Row>
            {  this.partnerPlanInfo() }
          </Row>}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.auth.tenant || {},
  Auth: state.auth.Auth,
  cognitoUser: state.auth.cognitoUser,
  activities: state.activity.activities,
  billingHistory: (state.settings.billingHistory || []).filter(invoice => parseFloat(_.get(invoice, 'lines.data[0].amount', 0)) !== 0) || []
});

const mapDispatchToProps = dispatch => 
bindActionCreators({
  getInvoices
}, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Billing));
